import { FC, useState } from "react";
import { Trash2, Loader, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useRemovePaymentMethodService } from "../services/use-remove-payment-method-service";

interface Props {
  methodId: string | number;
  contractId: string | number;
  invalidateKey: unknown[];
  onRemoved: () => void;
}

const SHOW_REMOVE_BUTTON = false;

const RemovePaymentMethodButton: FC<Props> = (props) => {
  const { methodId, contractId, invalidateKey, onRemoved } = props;
  const { t } = useTranslation(["checkout"]);
  const [open, setOpen] = useState(false);

  const { mutate: remove, isPending, error, reset } = useRemovePaymentMethodService(
    contractId,
    invalidateKey,
  );

  const handleConfirm = () => {
    remove(methodId, {
      onSuccess: () => {
        setOpen(false);
        onRemoved();
      },
    });
  };

  if (!SHOW_REMOVE_BUTTON) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-auto p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
      >
        <Trash2 className="size-4" />
      </button>

      <Dialog open={open} onOpenChange={(val) => { setOpen(val); reset(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("checkout:remove-method-title", "Remove payment method")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "checkout:remove-method-description",
                "Are you sure you want to remove this payment method? This action cannot be undone.",
              )}
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <span>{error.message}</span>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              {t("checkout:remove-method-cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isPending}
            >
              {isPending && <Loader className="size-4 animate-spin" />}
              {t("checkout:remove-method-confirm", "Remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RemovePaymentMethodButton;
